import React, { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  Users,
  TreePine,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Plus,
  Save,
  Trash2,
  X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

import { roleLabels, dayTypeLabels, priorityColors } from './mesaiConstants';
import { useRoleFlags } from '@/auth/useRoleFlags';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const PlanDetailContent = ({ 
  plan, 
  userRole, 
  personnel, 
  parks, 
  canApprovePlan, 
  onClose, 
  onUpdate, 
  onApprove, 
  onReject,
  getFilteredParks,
  getFilteredPersonnel 
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [isEditing, setIsEditing] = useState(false);
  const [editedAssignments, setEditedAssignments] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]); // Bekleyen değişiklikler

  // Temel rol kontrolü - park_gorevlisi ve çim_ekibi için kısıtlı görünüm
  const isBasicRole = ['park_gorevlisi'].includes(userRole);

  // Düzenleme moduna geçince mevcut atamaları kopyala
  const startEditing = () => {
    setEditedAssignments(JSON.parse(JSON.stringify(plan.assignments || [])));
    setPendingChanges([]);
    setIsEditing(true);
  };

  // Mevcut kullanıcı bilgisi
  const getCurrentUser = () => {
    const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
    return {
      id: auth.personnelId,
      name: auth.personnelName,
      role: auth.userRole
    };
  };

  // Rol başlığı
  const getRoleTitle = (role) => {
    const titles = {
      'formen': 'Formen',
      'bolge_muhendisi': 'Bölge Müh.',
      'mudur_yardimcisi': 'Müd. Yrd.',
      'mudur': 'Müdür',
      'park_gorevlisi': 'Park Gör.'
    };
    return titles[role] || role;
  };

  // Düzenleme yetkisi kontrolü
  const canEdit = () => {
    const currentUser = getCurrentUser();
    // Oluşturan kendi planını taslak durumundayken düzenleyebilir
    if (plan.status === 'taslak' && plan.created_by === currentUser.id) return true;
    
    // Üst amirler ilgili onay aşamasındayken düzenleyebilir
    // Bölge mühendisi: onay_bekliyor durumunda
    if (plan.status === 'onay_bekliyor' && isBolgeMuhendisi) return true;
    // Müdür yardımcısı: bolge_muhendisi_onayladi veya onay_bekliyor (eğer bölge müh. yoksa)
    if (['onay_bekliyor', 'bolge_muhendisi_onayladi'].includes(plan.status) && isMudurYardimcisi) return true;
    // Müdür: her zaman düzenleyebilir (onaylı hariç)
    if (!['onaylandi', 'reddedildi'].includes(plan.status) && isMudur) return true;
    
    return false;
  };
  
  // Onay yetkisi kontrolü
  const canApprove = () => {
    const creatorRole = plan.created_by_role;
    
    // MÜDÜR YARDIMCISI OLUŞTURDU → Sadece kendisi onaylayabilir (veya müdür)
    if (creatorRole === 'mudur_yardimcisi') {
      // Oluşturan kendisi ise onaylayabilir
      if (plan.created_by === getCurrentUser().id && plan.status !== 'onaylandi') return true;
      // Müdür de onaylayabilir
      if (isMudur && plan.status !== 'onaylandi') return true;
      return false;
    }
    
    // MÜDÜR OLUŞTURDU → Sadece kendisi onaylayabilir
    if (creatorRole === 'mudur') {
      if (plan.created_by === getCurrentUser().id && plan.status !== 'onaylandi') return true;
      return false;
    }
    
    // BM OLUŞTURDU → BM onaylar, sonra Müdür Yardımcısı son onay
    if (creatorRole === 'bolge_muhendisi') {
      // BM kendisi ilk onayı verebilir
      if (plan.created_by === getCurrentUser().id && plan.status === 'taslak') return true;
      if (plan.created_by === getCurrentUser().id && plan.status === 'onay_bekliyor') return true;
      // BM onayladıktan sonra Müdür Yardımcısı son onay
      if (plan.status === 'bolge_muhendisi_onayladi' && isMudurYardimcisi) return true;
      // Müdür her zaman onaylayabilir
      if (isMudur && plan.status !== 'onaylandi') return true;
      return false;
    }
    
    // FORMEN OLUŞTURDU → Hiyerarşik onay
    if (creatorRole === 'formen') {
      // Formen kendisi taslağı onaya gönderebilir
      if (plan.created_by === getCurrentUser().id && plan.status === 'taslak') return true;
      // BM onaylayabilir
      if (plan.status === 'onay_bekliyor' && isBolgeMuhendisi) return true;
      // BM onayladıktan sonra Müdür Yardımcısı
      if (plan.status === 'bolge_muhendisi_onayladi' && isMudurYardimcisi) return true;
      // Müdür Yardımcısı onayladıktan sonra Müdür (opsiyonel - direkt onay da olabilir)
      if (plan.status === 'mudur_yardimcisi_onayladi' && isMudur) return true;
      // Müdür her zaman onaylayabilir
      if (isMudur && plan.status !== 'onaylandi') return true;
      return false;
    }
    
    // Eski mantık (fallback)
    if (plan.status === 'onay_bekliyor' && isBolgeMuhendisi) return true;
    if (['onay_bekliyor', 'bolge_muhendisi_onayladi'].includes(plan.status) && isMudurYardimcisi) return true;
    if (!['taslak', 'onaylandi', 'reddedildi'].includes(plan.status) && isMudur) return true;
    
    return false;
  };

  // Park ekle
  const addPark = () => {
    const filteredParks = getFilteredParks();
    const usedParkIds = editedAssignments.map(a => a.park_id);
    const availableParks = filteredParks.filter(p => !usedParkIds.includes(p.id));
    
    if (availableParks.length === 0) {
      toast.error('Eklenebilecek park kalmadı');
      return;
    }
    
    setEditedAssignments([...editedAssignments, { 
      park_id: '', 
      park_name: '', 
      personnel: [] 
    }]);
  };

  // Park güncelle
  const updateParkSelection = (index, parkId) => {
    const park = parks.find(p => p.id === parkId);
    const newAssignments = [...editedAssignments];
    newAssignments[index] = {
      ...newAssignments[index],
      park_id: parkId,
      park_name: park?.name || ''
    };
    setEditedAssignments(newAssignments);
  };

  // Park sil
  const removePark = (index) => {
    const removedPark = editedAssignments[index];
    const currentUser = getCurrentUser();
    
    if (removedPark.park_name) {
      setPendingChanges([...pendingChanges, {
        type: 'park_removed',
        description: `${removedPark.park_name} parkı ${getRoleTitle(currentUser.role)} ${currentUser.name} tarafından kaldırıldı`
      }]);
    }
    
    const newAssignments = editedAssignments.filter((_, i) => i !== index);
    setEditedAssignments(newAssignments);
  };

  // Personel ekle/çıkar
  const togglePersonnel = (parkIndex, person) => {
    const currentUser = getCurrentUser();
    const newAssignments = [...editedAssignments];
    const assignment = newAssignments[parkIndex];
    
    const existingIndex = assignment.personnel?.findIndex(p => p.personnel_id === person.id);
    
    if (existingIndex >= 0) {
      // Personeli çıkar
      const removedPerson = assignment.personnel[existingIndex];
      assignment.personnel = assignment.personnel.filter((_, i) => i !== existingIndex);
      
      setPendingChanges([...pendingChanges, {
        type: 'personnel_removed',
        description: `${removedPerson.personnel_name} (${assignment.park_name}) - ${getRoleTitle(currentUser.role)} ${currentUser.name} tarafından çıkarıldı`
      }]);
    } else {
      // Personel ekle
      if (!assignment.personnel) assignment.personnel = [];
      assignment.personnel.push({
        personnel_id: person.id,
        personnel_name: person.name,
        personnel_role: person.role
      });
      
      setPendingChanges([...pendingChanges, {
        type: 'personnel_added',
        description: `${person.name} (${assignment.park_name}) - ${getRoleTitle(currentUser.role)} ${currentUser.name} tarafından eklendi`
      }]);
    }
    
    setEditedAssignments(newAssignments);
  };

  // Değişiklikleri kaydet
  const saveChanges = async () => {
    if (pendingChanges.length === 0) {
      toast.info('Değişiklik yapılmadı');
      setIsEditing(false);
      return;
    }
    
    const changeDescription = pendingChanges.map(c => c.description).join(' | ');
    await onUpdate(editedAssignments, changeDescription);
    setIsEditing(false);
    setPendingChanges([]);
  };

  // Durum badge'i
  const getStatusBadge = (status) => {
    const statusConfig = {
      'taslak': { label: 'Taslak', className: 'bg-gray-500' },
      'onay_bekliyor': { label: 'Bölge Müh. Onayı Bekliyor', className: 'bg-yellow-500' },
      'bolge_muhendisi_onayladi': { label: 'Müd. Yrd. Onayı Bekliyor', className: 'bg-orange-500' },
      'mudur_yardimcisi_onayladi': { label: 'Müdür Onayı Bekliyor', className: 'bg-blue-500' },
      'onaylandi': { label: 'Onaylandı', className: 'bg-green-500' },
      'reddedildi': { label: 'Reddedildi', className: 'bg-red-500' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Üst Bilgiler */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-gray-500">Tarih</Label>
          <p className="font-medium">{format(parseISO(plan.target_date), 'd MMMM yyyy, EEEE', { locale: tr })}</p>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Durum</Label>
          <div>{getStatusBadge(plan.status)}</div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Oluşturan</Label>
          <p>{plan.created_by_name} ({getRoleTitle(plan.created_by_role)})</p>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Oluşturma Tarihi</Label>
          <p>{format(parseISO(plan.created_at), 'd MMM yyyy HH:mm', { locale: tr })}</p>
        </div>
      </div>

      {/* Mesaiye Gelen Formen ve BM Bilgisi */}
      {(plan.target_formen_id || plan.target_bolge_muhendisi_id) && (
        <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Alan Denetimi Yapacaklar
          </h4>
          <div className="flex flex-wrap gap-3">
            {plan.target_formen_id && (() => {
              const formen = personnel.find(p => p.id === plan.target_formen_id);
              return formen ? (
                <div className="flex items-center gap-2 bg-purple-100 px-3 py-1.5 rounded-full">
                  <span className="text-xs font-medium text-purple-700">Formen:</span>
                  <span className="text-sm font-semibold text-purple-900">{formen.name}</span>
                </div>
              ) : null;
            })()}
            {plan.target_bolge_muhendisi_id && (() => {
              const bm = personnel.find(p => p.id === plan.target_bolge_muhendisi_id);
              return bm ? (
                <div className="flex items-center gap-2 bg-cyan-100 px-3 py-1.5 rounded-full">
                  <span className="text-xs font-medium text-cyan-700">Bölge Müh.:</span>
                  <span className="text-sm font-semibold text-cyan-900">{bm.name}</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Park Atamaları */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label>Park ve Personel Atamaları</Label>
          {!isEditing && canEdit() && (
            <Button size="sm" variant="outline" onClick={startEditing}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Düzenle
            </Button>
          )}
          {isEditing && (
            <Button size="sm" variant="outline" onClick={addPark}>
              <Plus className="h-4 w-4 mr-1" />
              Park Ekle
            </Button>
          )}
        </div>

        {/* Görüntüleme Modu */}
        {!isEditing && (
          <div className="space-y-3 mt-2">
            {plan.assignments?.map((assignment, index) => (
              <div key={index} className="p-3 border rounded-lg bg-gray-50">
                <p className="font-semibold text-green-700">🏞️ {assignment.park_name}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {assignment.personnel?.map(p => (
                    <Badge key={p.personnel_id} variant="outline">
                      {p.personnel_name}
                    </Badge>
                  ))}
                  {(!assignment.personnel || assignment.personnel.length === 0) && (
                    <span className="text-sm text-gray-400 italic">Personel atanmamış</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Düzenleme Modu */}
        {isEditing && (
          <div className="space-y-3 mt-2">
            {editedAssignments.map((assignment, parkIndex) => (
              <div key={parkIndex} className="p-3 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2 mb-2">
                  <SearchableSelect
                    value={assignment.park_id}
                    onChange={(value) => updateParkSelection(parkIndex, value)}
                    showAllOption={false}
                    placeholder="Park seçin..."
                    searchPlaceholder="Park ara..."
                    className="flex-1 bg-white"
                    options={getFilteredParks()
                      .filter(p => p.id === assignment.park_id || !editedAssignments.some(a => a.park_id === p.id))
                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                      .map(park => ({ id: park.id, name: park.name }))}
                  />
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => removePark(parkIndex)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                {assignment.park_id && (
                  <div className="mt-2">
                    <Label className="text-xs">Personel ({assignment.personnel?.length || 0} seçili)</Label>
                    <div className="flex flex-wrap gap-1 mt-1 max-h-32 overflow-y-auto">
                      {getFilteredPersonnel().map(person => {
                        const isSelected = assignment.personnel?.some(p => p.personnel_id === person.id);
                        return (
                          <Badge 
                            key={person.id}
                            variant={isSelected ? 'default' : 'outline'}
                            className={`cursor-pointer ${isSelected ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-gray-100'}`}
                            onClick={() => togglePersonnel(parkIndex, person)}
                          >
                            {isSelected && '✓ '}{person.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {editedAssignments.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                Henüz park ataması yok. &quot;Park Ekle&quot; butonuna tıklayın.
              </p>
            )}

            {/* Bekleyen Değişiklikler */}
            {pendingChanges.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Label className="text-xs text-yellow-700">📝 Bekleyen Değişiklikler ({pendingChanges.length})</Label>
                <div className="mt-1 space-y-1 max-h-24 overflow-y-auto">
                  {pendingChanges.map((change, idx) => (
                    <p key={idx} className="text-xs text-yellow-800">
                      {change.type === 'personnel_removed' && '❌ '}
                      {change.type === 'personnel_added' && '✅ '}
                      {change.type === 'park_removed' && '🏞️❌ '}
                      {change.description}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Düzenleme Butonları */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsEditing(false);
                  setPendingChanges([]);
                }}
                className="flex-1"
              >
                İptal
              </Button>
              <Button 
                onClick={saveChanges}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={pendingChanges.length === 0}
              >
                <Save className="h-4 w-4 mr-2" />
                Değişiklikleri Kaydet
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Değişiklik Geçmişi - Sadece amir rolleri için */}
      {!isBasicRole && plan.change_history?.length > 0 && (
        <div className="border-t pt-4">
          <Label>📋 Değişiklik Geçmişi</Label>
          <div className="space-y-2 mt-2 max-h-32 overflow-y-auto">
            {plan.change_history.map((entry, index) => (
              <div key={index} className="text-sm p-2 bg-gray-50 rounded border">
                <p className="text-gray-700">{entry.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {entry.by_name} ({getRoleTitle(entry.by_role)}) - {format(parseISO(entry.at), 'd MMM HH:mm', { locale: tr })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Onay Geçmişi - Amir rolleri için detaylı, temel roller için sadeleştirilmiş */}
      {plan.approval_history?.length > 0 && (
        <div className="border-t pt-4">
          {isBasicRole ? (
            // Park Görevlisi / Çim Ekibi için sadece son onay bilgisi
            plan.status === 'onaylandi' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-green-700 font-medium">
                  Bu plan {plan.final_approved_by_name || plan.approved_by_name} tarafından onaylandı
                </p>
              </div>
            )
          ) : (
            // Amir rolleri için detaylı onay geçmişi
            <>
              <Label>📋 Onay Geçmişi</Label>
              <div className="space-y-2 mt-2">
                {plan.approval_history.map((entry, index) => {
                  const actionLabels = {
                'onaylandi': { icon: '✅', label: 'Son Onay Verildi', color: 'text-green-600' },
                'reddedildi': { icon: '❌', label: 'Reddedildi', color: 'text-red-600' },
                'onay_bekliyor': { icon: '📤', label: 'Onaya Gönderildi', color: 'text-blue-600' },
                'bolge_muhendisi_onayladi': { icon: '✓', label: 'Onayladı', color: 'text-green-600' },
                'mudur_yardimcisi_onayladi': { icon: '✓', label: 'Onayladı', color: 'text-green-600' },
                'mudur_onayladi': { icon: '✅', label: 'Son Onay Verildi', color: 'text-green-600' }
              };
              const actionConfig = actionLabels[entry.action] || { icon: '📝', label: entry.action, color: 'text-gray-600' };
              
              return (
                <div key={index} className="text-sm flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <span className={actionConfig.color}>{actionConfig.icon}</span>
                  <span className="font-medium">{entry.by_name}</span>
                  <span className="text-gray-500">({getRoleTitle(entry.by_role)})</span>
                  <span className={actionConfig.color}>- {actionConfig.label}</span>
                  <span className="text-gray-400 ml-auto">
                    {format(parseISO(entry.at), 'd MMM HH:mm', { locale: tr })}
                  </span>
                  {entry.reason && <span className="text-red-500">({entry.reason})</span>}
                </div>
              );
            })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Onay/Red Butonları - Hiyerarşik onay sistemine göre */}
      {!isEditing && canApprove() && (
        <div className="flex gap-2 pt-4 border-t">
          <Button 
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={onApprove}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {plan.created_by_role === 'mudur_yardimcisi' || plan.created_by_role === 'mudur'
              ? 'Onayla' 
              : isMudurYardimcisi && ['onay_bekliyor', 'bolge_muhendisi_onayladi'].includes(plan.status) 
                ? 'Onayla (Son Onay)' 
                : isMudur 
                  ? 'Onayla (Son Onay)'
                  : 'Onayla ve Üst Makama Gönder'
            }
          </Button>
          <Button 
            variant="outline"
            className="flex-1 text-orange-600 border-orange-400"
            onClick={onReject}
          >
            İptal
          </Button>
        </div>
      )}

      {/* Onaylanmış plan için sadece görüntüleme */}
      {!isEditing && plan.status === 'onaylandi' && (
        <div className="flex gap-2 pt-4 border-t">
          <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
            <CheckCircle className="h-5 w-5 text-green-600 inline mr-2" />
            <span className="text-green-700 font-medium">
              Bu plan {plan.final_approved_by_name || plan.approved_by_name} tarafından onaylandı
            </span>
          </div>
        </div>
      )}

      {/* Taslak ise - Oluşturana göre farklı butonlar */}
      {!isEditing && plan.status === 'taslak' && plan.created_by === getCurrentUser().id && (
        <div className="flex gap-2 pt-4 border-t">
          <Button 
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Kapat
          </Button>
          {/* Müdür/Müdür Yardımcısı kendi oluşturduğu planı direkt onaylar */}
          {['mudur', 'mudur_yardimcisi'].includes(userRole) ? (
            <>
              <Button 
                variant="outline"
                className="flex-1 text-orange-600 border-orange-400"
                onClick={async () => {
                  // İptal diyaloğu aç
                  if (window.confirm('Bu planı iptal etmek istediğinize emin misiniz?')) {
                    try {
                      await axios.delete(`${API}/api/overtime-draft-plans/${plan.id}`);
                      toast.success('Plan iptal edildi');
                      onClose();
                    } catch (error) {
                      toast.error('İptal başarısız: ' + (error.response?.data?.detail || error.message));
                    }
                  }
                }}
              >
                <X className="h-4 w-4 mr-2" />
                İptal
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={async () => {
                  try {
                    const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
                    await axios.put(
                      `${API}/api/overtime-draft-plans/${plan.id}/approve?approved_by=${auth.personnelId}&approved_by_name=${encodeURIComponent(auth.personnelName || '')}&approved_by_role=${auth.userRole}`
                    );
                    toast.success('Plan onaylandı ve mesai çizelgesi oluşturuldu');
                    onClose();
                  } catch (error) {
                    toast.error('Onaylama başarısız: ' + (error.response?.data?.detail || error.message));
                  }
                }}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Onayla
              </Button>
            </>
          ) : (
            /* Diğer roller için onaya gönder */
            <Button 
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                try {
                  const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
                  await axios.put(
                    `${API}/api/overtime-draft-plans/${plan.id}/submit-for-approval?submitted_by=${auth.personnelId}&submitted_by_name=${encodeURIComponent(auth.personnelName || '')}&submitted_by_role=${auth.userRole}`
                  );
                  toast.success('Plan onaya gönderildi');
                  onClose();
                } catch (error) {
                  toast.error('Onaya gönderilemedi: ' + (error.response?.data?.detail || error.message));
                }
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Onaya Gönder
            </Button>
          )}
        </div>
      )}
    </div>
  );
};


export default PlanDetailContent;
